import { act, fireEvent, render, screen, waitFor } from '@testing-library/react'
import { makeTicket as makeLibraTicket } from 'features/libra/dashboard/ticketDashboard/libra/testFixtures'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import { deferred } from 'features/libra/stepComponents/zoneAnalysis/zoneSaveTestHelpers'
import {
  StepValidationProvider,
  useStepValidation,
} from 'features/libra/StepValidationContext'
import type {
  LibraTicket,
  UpdateTicketArgs,
} from 'features/libra/ticket/libraTicketsApi'
import type { TicketWriteFn } from 'features/libra/ticket/TicketWriterContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import type { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import { type ReactNode, useCallback, useRef, useState } from 'react'
import { afterEach, beforeEach, describe, expect, it, vi } from 'vitest'

const zoneMocks = vi.hoisted<{
  fieldValues: FieldValues
  ticketId: string
  committedTicket?: LibraTicket
  getCommittedTicket: () => LibraTicket
  enqueue: ReturnType<typeof vi.fn>
  errorNotification: ReturnType<typeof vi.fn>
  successNotification: ReturnType<typeof vi.fn>
}>(() => ({
  fieldValues: {},
  ticketId: 'zone-ticket',
  getCommittedTicket: () => {
    throw new Error('Expected a committed ticket')
  },
  enqueue: vi.fn(),
  errorNotification: vi.fn(),
  successNotification: vi.fn(),
}))

vi.hoisted(() => {
  vi.stubGlobal(
    'matchMedia',
    vi.fn(() => ({
      matches: false,
      addListener: vi.fn(),
      removeListener: vi.fn(),
      addEventListener: vi.fn(),
      removeEventListener: vi.fn(),
    }))
  )
})

vi.mock('features/libra/ticket/TicketAccessContext', () => ({
  useTicketAccess: () => ({
    fieldValues: zoneMocks.fieldValues,
    readOnly: false,
    ticketId: zoneMocks.ticketId,
    isLoading: false,
    ticketTags: { unit: 'unit-1' },
  }),
}))

vi.mock('features/libra/ticket/TicketWriterContext', () => ({
  useTicketWriter: () => ({
    get committedTicket() {
      const committedTicket = zoneMocks.committedTicket
      if (!committedTicket) throw new Error('Expected a committed ticket')
      return committedTicket
    },
    getCommittedTicket: zoneMocks.getCommittedTicket,
    enqueue: zoneMocks.enqueue,
    isSaving: false,
  }),
}))

vi.mock('features/libra/stepComponents/useLibraAssetModelData', () => ({
  useLibraAssetModelData: () => ({
    unit: undefined,
    assetModel: undefined,
    siteName: '',
    components: [],
    inspections: [],
    componentDataFrames: [],
    isLoading: false,
  }),
}))

vi.mock('features/notifications', () => ({
  useGeckoNotifications: () => ({
    errorNotification: zoneMocks.errorNotification,
    successNotification: zoneMocks.successNotification,
  }),
}))

vi.mock('features/libra/ticket/TicketStateContext', () => ({
  useActiveStepId: () => 'zones',
}))

vi.mock('features/libra/comments/CommentIndicator', () => ({
  COMMENT_INDICATOR_GUTTER: '32px',
  CommentIndicator: ({ children }: { children: ReactNode }) => children,
}))

vi.mock('features/libra/components/InputProvenanceRow', () => ({
  InputProvenanceRow: () => null,
}))

vi.mock('features/libra/comments/CommentsProvider', () => ({
  useReveal: () => undefined,
}))

vi.mock('features/libra/document/LibraDocumentContext', () => ({
  useLibraDocument: () => ({
    selectedDocumentId: 'drawing-1',
    setSelectedDocumentId: vi.fn(),
  }),
}))

vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/DamageMechanismsPanel',
  () => ({
    DamageMechanismsPanel: () => null,
  })
)
vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/DesignFunctionPanel',
  () => ({
    DesignFunctionPanel: () => null,
  })
)
vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/NiiStrategyPanel',
  () => ({
    NiiStrategyPanel: () => null,
  })
)
vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/RbaInformationPanel',
  () => ({
    RbaInformationPanel: () => null,
  })
)
vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/ZoneDescriptionPanel',
  () => ({
    ZoneDescriptionPanel: () => null,
  })
)
vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/ZoneSummaryCard',
  () => ({
    ZoneSummaryCard: () => null,
  })
)
vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/ZoneTabAiPrefill',
  () => ({
    ZonePrefillAllBar: () => null,
    ZoneTabAiPrefillBanner: () => null,
  })
)

vi.mock(
  'features/libra/stepComponents/zoneAnalysis/components/ZoneTicketAiPrefillActions',
  () => ({ ZoneBreakdownAiPrefillButton: () => null })
)

vi.mock('./niiReview/ZoneAnalysisReviewPanel', () => ({
  ZoneAnalysisReviewPanel: () => null,
}))

vi.mock('./zoneAnalysis/components/NiiZones', async () => {
  const { useZoneAnalysisData } =
    await import('features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider')
  const { zoneParamsSchema } =
    await import('features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi')
  const ZoneEditorProbe = () => {
    const data = useZoneAnalysisData()
    const invokeSave = (save: () => Promise<void>) => {
      void save().catch(() => undefined)
    }
    return (
      <div>
        <div data-testid='zone-values'>{JSON.stringify(data.zones)}</div>
        <div data-testid='zone-page-dirty'>{String(data.hasChanges)}</div>
        <div data-testid='zone-b-dirty'>{String(data.isZoneDirty('b'))}</div>
        <div data-testid='zone-saving'>{String(data.isSaving)}</div>
        <button
          onClick={() =>
            data.setZones((current) =>
              current.map((zone) =>
                zone.id === 'a' ? { ...zone, title: 'A draft' } : zone
              )
            )
          }>
          Edit zone A
        </button>
        <button
          onClick={() =>
            data.setZones((current) =>
              current.map((zone) =>
                zone.id === 'a' ? { ...zone, title: 'A' } : zone
              )
            )
          }>
          Restore zone A
        </button>
        <button
          onClick={() =>
            data.setZones((current) =>
              current.map((zone) =>
                zone.id === 'b' ? { ...zone, title: 'B draft' } : zone
              )
            )
          }>
          Edit zone B
        </button>
        <button
          onClick={() =>
            data.setZones((current) => [
              ...current,
              {
                ...zoneParamsSchema.parse({ id: 'ai', title: 'AI' }),
                ai_suggested: true,
              },
            ])
          }>
          Add AI proposal
        </button>
        <button
          onClick={() =>
            data.setZones((current) => [
              ...current,
              {
                ...zoneParamsSchema.parse({ id: 'ai-late', title: 'Late AI' }),
                ai_suggested: true,
              },
            ])
          }>
          Complete AI proposal
        </button>
        <button onClick={() => invokeSave(() => data.saveZone('a'))}>
          Save zone A
        </button>
        <button
          onClick={() =>
            data.setZones((current) =>
              current.map((zone) =>
                zone.id === 'b' ? { ...zone, title: 'B later' } : zone
              )
            )
          }>
          Edit zone B later
        </button>
      </div>
    )
  }
  return { NiiZones: ZoneEditorProbe }
})

vi.mock('./zoneAnalysis/components/AssetModelZoneEditor', () => ({
  AssetModelZoneEditor: () => null,
}))

import { ThemeProvider } from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'

import { Component as ZoneAnalysisComponent } from './ZoneAnalysis'

function GuardProbe({
  save,
  onContinue,
}: {
  save: () => Promise<void>
  onContinue: () => void
}) {
  const [hasChanges, setHasChanges] = useState(true)
  const hasChangesRef = useRef(hasChanges)
  hasChangesRef.current = hasChanges
  const canProceed = useCallback(() => !hasChangesRef.current, [])
  const guard = useRegisterSaveGuard({
    hasUnsavedEdits: hasChanges,
    save,
    canProceed,
  })
  const { navigationGuard } = useStepValidation()

  return (
    <>
      <div data-testid='guard-open'>{String(guard.open)}</div>
      <div data-testid='guard-ready'>{String(Boolean(navigationGuard))}</div>
      <button onClick={() => navigationGuard?.(onContinue)}>Navigate</button>
      <button
        onClick={() => {
          void guard.onSaveAndContinue().catch(() => {})
        }}>
        Save and continue
      </button>
      <button onClick={() => guard.onCancel()}>Cancel</button>
      <button onClick={() => setHasChanges(false)}>Saved current draft</button>
      <button onClick={() => setHasChanges(true)}>Edit a later draft</button>
    </>
  )
}

function renderGuard(save: () => Promise<void>, onContinue: () => void) {
  return render(
    <StepValidationProvider>
      <GuardProbe save={save} onContinue={onContinue} />
    </StepValidationProvider>
  )
}

function getPatchValues(
  patch: Omit<UpdateTicketArgs, 'ticketId'> | undefined
): FieldValues {
  if (!patch?.fieldValues) throw new Error('Expected a fieldValues patch')
  return patch.fieldValues
}

function acknowledgePatch(patch: Omit<UpdateTicketArgs, 'ticketId'>) {
  const latest = getCommittedTicket()
  zoneMocks.committedTicket = {
    ...latest,
    ...patch,
  }
}

function getCommittedTicket() {
  const committedTicket = zoneMocks.committedTicket
  if (!committedTicket) throw new Error('Expected a committed ticket')
  return committedTicket
}

function ZoneBoundaryHarness({ onContinue }: { onContinue: () => void }) {
  const { navigationGuard } = useStepValidation()
  const [, setValidationResult] = useState<StepValidationResult>(undefined)
  return (
    <>
      <div data-testid='navigation-guard-ready'>
        {String(Boolean(navigationGuard))}
      </div>
      <button onClick={() => navigationGuard?.(onContinue)}>
        Attempt navigation
      </button>
      <AiPrefillButton>Run AI prefill</AiPrefillButton>
      <ZoneAnalysisComponent
        name='NII Zones'
        config={{ zoneFieldName: 'zones' }}
        setValidationResult={setValidationResult}
      />
    </>
  )
}

function renderZoneBoundary(onContinue: () => void) {
  const page = () => (
    <ThemeProvider theme={GeckoTheme}>
      <StepValidationProvider>
        <ZoneBoundaryHarness onContinue={onContinue} />
      </StepValidationProvider>
    </ThemeProvider>
  )
  const view = render(page())
  return { view, rerender: () => view.rerender(page()) }
}

beforeEach(() => {
  zoneMocks.ticketId = 'zone-ticket'
  zoneMocks.fieldValues = {
    zones: {
      value: [
        { id: 'a', title: 'A', damage_mechanisms: [] },
        { id: 'b', title: 'B', damage_mechanisms: [] },
      ],
      source: null,
    },
    zone_breakdown_justification: { value: 'saved justification' },
  }
  zoneMocks.committedTicket = makeLibraTicket({
    id: zoneMocks.ticketId,
    workflowId: 'workflow-1',
    title: 'Zone test ticket',
    fieldValues: zoneMocks.fieldValues,
    stepId: 'zones',
    tags: {},
  })
  zoneMocks.getCommittedTicket = () => {
    const committedTicket = zoneMocks.committedTicket
    if (!committedTicket) throw new Error('Expected a committed ticket')
    return committedTicket
  }
  zoneMocks.enqueue.mockReset()
  zoneMocks.errorNotification.mockReset()
  zoneMocks.successNotification.mockReset()
  vi.stubGlobal(
    'matchMedia',
    vi.fn(() => ({
      matches: false,
      addListener: vi.fn(),
      removeListener: vi.fn(),
      addEventListener: vi.fn(),
      removeEventListener: vi.fn(),
    }))
  )
  vi.stubGlobal(
    'ResizeObserver',
    class {
      observe() {}
      unobserve() {}
      disconnect() {}
    }
  )
})

afterEach(() => vi.unstubAllGlobals())

describe('ZoneAnalysis navigation save guard', () => {
  it.each(['scoped', 'page'])(
    'keeps navigation and AI prefill guarded when a %s save finishes after a revert',
    async (saveScope) => {
      const firstSave = deferred()
      const patches: Omit<UpdateTicketArgs, 'ticketId'>[] = []
      zoneMocks.enqueue.mockImplementation((write: TicketWriteFn) => {
        const patch = write(getCommittedTicket())
        if (!patch) return Promise.resolve(null)
        patches.push(patch)
        return patches.length === 1
          ? firstSave.promise.then(() => {
              acknowledgePatch(patch)
              return patch
            })
          : Promise.resolve(patch).then((applied) => {
              acknowledgePatch(applied)
              return applied
            })
      })
      const onContinue = vi.fn()
      renderZoneBoundary(onContinue)
      const aiPrefillButton = () =>
        screen.getByRole<HTMLButtonElement>('button', {
          name: 'Run AI prefill',
        })

      expect(aiPrefillButton().disabled).toBe(false)
      fireEvent.click(screen.getByRole('button', { name: 'Edit zone A' }))
      expect(aiPrefillButton().disabled).toBe(true)
      if (saveScope === 'scoped') {
        fireEvent.click(screen.getByRole('button', { name: 'Save zone A' }))
      } else {
        fireEvent.click(screen.getByRole('button', { name: 'Save' }))
      }
      await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(1))
      expect(getPatchValues(patches[0]).zones?.value).toEqual([
        expect.objectContaining({ id: 'a', title: 'A draft' }),
        expect.objectContaining({ id: 'b', title: 'B' }),
      ])

      fireEvent.click(screen.getByRole('button', { name: 'Restore zone A' }))
      expect(screen.getByTestId('zone-page-dirty').textContent).toBe('false')
      expect(screen.getByTestId('zone-saving').textContent).toBe('true')
      expect(aiPrefillButton().disabled).toBe(true)
      fireEvent.click(
        screen.getByRole('button', { name: 'Attempt navigation' })
      )
      expect(screen.getByRole('dialog')).toBeTruthy()
      expect(
        screen.getByRole<HTMLButtonElement>('button', {
          name: 'Save & Continue',
        }).disabled
      ).toBe(true)
      expect(onContinue).not.toHaveBeenCalled()

      await act(async () => firstSave.resolve())
      await waitFor(() =>
        expect(screen.getByTestId('zone-page-dirty').textContent).toBe('true')
      )
      expect(aiPrefillButton().disabled).toBe(true)
      expect(screen.getByRole('dialog')).toBeTruthy()
      expect(onContinue).not.toHaveBeenCalled()

      fireEvent.click(screen.getByRole('button', { name: 'Save & Continue' }))
      await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(2))
      expect(getPatchValues(patches[1]).zones?.value).toEqual([
        expect.objectContaining({ id: 'a', title: 'A' }),
        expect.objectContaining({ id: 'b', title: 'B' }),
      ])
      await waitFor(() => expect(onContinue).toHaveBeenCalledTimes(1))
      await waitFor(() => expect(aiPrefillButton().disabled).toBe(false))
    }
  )

  it('does not continue when a scoped save rejects after the draft reverts', async () => {
    const pending = deferred()
    zoneMocks.enqueue.mockImplementation((write: TicketWriteFn) => {
      const patch = write(getCommittedTicket())
      if (!patch) return Promise.resolve(null)
      return pending.promise.then(() => {
        acknowledgePatch(patch)
        return patch
      })
    })
    const onContinue = vi.fn()
    renderZoneBoundary(onContinue)

    fireEvent.click(screen.getByRole('button', { name: 'Edit zone A' }))
    fireEvent.click(screen.getByRole('button', { name: 'Save zone A' }))
    await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(1))
    fireEvent.click(screen.getByRole('button', { name: 'Restore zone A' }))
    fireEvent.click(screen.getByRole('button', { name: 'Attempt navigation' }))
    expect(screen.getByRole('dialog')).toBeTruthy()
    expect(
      screen.getByRole<HTMLButtonElement>('button', {
        name: 'Save & Continue',
      }).disabled
    ).toBe(true)

    await act(async () => pending.reject(new Error('zone save failed')))
    await waitFor(() =>
      expect(zoneMocks.errorNotification).toHaveBeenCalledWith(
        'zone save failed'
      )
    )
    expect(screen.getByTestId('zone-values').textContent).toContain(
      '"id":"a","title":"A"'
    )
    expect(screen.getByTestId('zone-page-dirty').textContent).toBe('false')
    expect(screen.getByTestId('zone-saving').textContent).toBe('false')
    expect(onContinue).not.toHaveBeenCalled()
  })

  it('blocks navigation during scoped save, then requires explicit page saves for remaining and later edits', async () => {
    const gates = [deferred(), deferred(), deferred(), deferred()]
    const patches: Omit<UpdateTicketArgs, 'ticketId'>[] = []
    zoneMocks.enqueue.mockImplementation((write: TicketWriteFn) => {
      const patch = write(getCommittedTicket())
      if (!patch) return Promise.resolve(null)
      patches.push(patch)
      return gates[patches.length - 1].promise.then(() => {
        acknowledgePatch(patch)
        return patch
      })
    })
    const onContinue = vi.fn()
    const { rerender } = renderZoneBoundary(onContinue)

    fireEvent.click(screen.getByRole('button', { name: 'Edit zone A' }))
    fireEvent.click(screen.getByRole('button', { name: 'Edit zone B' }))
    fireEvent.click(screen.getByRole('button', { name: 'Add AI proposal' }))
    fireEvent.change(
      screen.getByPlaceholderText(/The vessel was split into three zones/),
      { target: { value: 'draft justification' } }
    )
    fireEvent.click(screen.getByRole('button', { name: 'Save zone A' }))
    await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(1))
    fireEvent.click(screen.getByRole('button', { name: 'Save zone A' }))
    await waitFor(() =>
      expect(zoneMocks.errorNotification).toHaveBeenCalledWith(
        'Another save is in progress. Wait for it to finish before saving again.'
      )
    )
    expect(zoneMocks.enqueue).toHaveBeenCalledTimes(1)

    const scopedFields = getPatchValues(patches[0])
    expect(scopedFields.zones?.value).toEqual([
      expect.objectContaining({ id: 'a', title: 'A draft' }),
      expect.objectContaining({ id: 'b', title: 'B' }),
    ])
    expect(scopedFields.zone_breakdown_justification).toEqual({
      value: 'saved justification',
    })
    expect(screen.getByTestId('zone-saving').textContent).toBe('true')
    expect(screen.getByTestId('zone-values').textContent).toContain('B draft')

    await waitFor(() =>
      expect(screen.getByTestId('navigation-guard-ready').textContent).toBe(
        'true'
      )
    )
    fireEvent.click(screen.getByRole('button', { name: 'Attempt navigation' }))
    const saveAndContinue = screen.getByRole<HTMLButtonElement>('button', {
      name: 'Save & Continue',
    })
    expect(saveAndContinue.disabled).toBe(true)

    await act(async () => gates[0].resolve())
    expect(screen.getByTestId('zone-saving').textContent).toBe('false')
    await waitFor(() =>
      expect(
        screen.getByRole<HTMLButtonElement>('button', {
          name: 'Save & Continue',
        }).disabled
      ).toBe(false)
    )
    zoneMocks.fieldValues = scopedFields
    rerender()
    expect(screen.getByTestId('zone-b-dirty').textContent).toBe('true')
    expect(
      screen.getByPlaceholderText<HTMLTextAreaElement>(
        /The vessel was split into three zones/
      ).value
    ).toBe('draft justification')

    fireEvent.click(screen.getByRole('button', { name: 'Save & Continue' }))
    await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(2))
    const pageFields = getPatchValues(patches[1])
    expect(pageFields.zones?.value).toEqual([
      expect.objectContaining({ id: 'a', title: 'A draft' }),
      expect.objectContaining({ id: 'b', title: 'B draft' }),
    ])
    expect(pageFields.zones?.value).not.toEqual(
      expect.arrayContaining([expect.objectContaining({ id: 'ai' })])
    )
    expect(pageFields.zone_breakdown_justification?.value).toBe(
      'draft justification'
    )

    await act(async () => gates[1].reject(new Error('page save failed')))
    await waitFor(() =>
      expect(zoneMocks.errorNotification).toHaveBeenCalledWith(
        'page save failed'
      )
    )
    expect(onContinue).not.toHaveBeenCalled()
    expect(screen.getByRole('button', { name: 'Save & Continue' })).toBeTruthy()

    fireEvent.click(screen.getByRole('button', { name: 'Save & Continue' }))
    await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(3))
    const retryFields = getPatchValues(patches[2])

    fireEvent.click(screen.getByRole('button', { name: 'Edit zone B later' }))
    fireEvent.click(
      screen.getByRole('button', { name: 'Complete AI proposal' })
    )
    fireEvent.change(
      screen.getByPlaceholderText(/The vessel was split into three zones/),
      { target: { value: 'later justification' } }
    )
    zoneMocks.fieldValues = retryFields
    rerender()
    await act(async () => gates[2].resolve())
    expect(onContinue).not.toHaveBeenCalled()
    expect(screen.getByTestId('zone-values').textContent).toContain('B later')
    expect(
      screen.getByPlaceholderText<HTMLTextAreaElement>(
        /The vessel was split into three zones/
      ).value
    ).toBe('later justification')

    fireEvent.click(screen.getByRole('button', { name: 'Save & Continue' }))
    await waitFor(() => expect(zoneMocks.enqueue).toHaveBeenCalledTimes(4))
    const finalFields = getPatchValues(patches[3])
    expect(finalFields.zones?.value).toEqual([
      expect.objectContaining({ id: 'a', title: 'A draft' }),
      expect.objectContaining({ id: 'b', title: 'B later' }),
    ])
    expect(finalFields.zone_breakdown_justification?.value).toBe(
      'later justification'
    )
    await act(async () => gates[3].resolve())
    await waitFor(() => expect(onContinue).toHaveBeenCalledTimes(1))
    expect(screen.getByTestId('zone-values').textContent).toContain('"id":"ai"')
    expect(screen.getByTestId('zone-values').textContent).toContain(
      '"id":"ai-late"'
    )
  })

  it('keeps navigation pending when a newer draft remains after page-save acknowledgement', async () => {
    const pending = deferred()
    const onContinue = vi.fn()
    const save = vi.fn(() => pending.promise)
    renderGuard(save, onContinue)

    await waitFor(() =>
      expect(screen.getByTestId('guard-ready').textContent).toBe('true')
    )
    fireEvent.click(screen.getByRole('button', { name: 'Navigate' }))
    expect(screen.getByTestId('guard-open').textContent).toBe('true')
    fireEvent.click(screen.getByRole('button', { name: 'Save and continue' }))
    await waitFor(() => expect(save).toHaveBeenCalledTimes(1))
    fireEvent.click(screen.getByRole('button', { name: 'Saved current draft' }))
    fireEvent.click(screen.getByRole('button', { name: 'Edit a later draft' }))

    await act(async () => pending.resolve())
    expect(onContinue).not.toHaveBeenCalled()
    expect(screen.getByTestId('guard-open').textContent).toBe('true')
  })

  it('does not reuse a canceled continuation for a later navigation request', async () => {
    const pending = deferred()
    const onContinue = vi.fn()
    const save = vi.fn(() => pending.promise)
    renderGuard(save, onContinue)

    await waitFor(() =>
      expect(screen.getByTestId('guard-ready').textContent).toBe('true')
    )
    fireEvent.click(screen.getByRole('button', { name: 'Navigate' }))
    fireEvent.click(screen.getByRole('button', { name: 'Save and continue' }))
    await waitFor(() => expect(save).toHaveBeenCalledTimes(1))
    fireEvent.click(screen.getByRole('button', { name: 'Cancel' }))
    fireEvent.click(screen.getByRole('button', { name: 'Navigate' }))
    expect(screen.getByTestId('guard-open').textContent).toBe('true')
    fireEvent.click(screen.getByRole('button', { name: 'Saved current draft' }))

    await act(async () => pending.resolve())
    expect(onContinue).not.toHaveBeenCalled()
    expect(screen.getByTestId('guard-open').textContent).toBe('true')
  })

  it('proceeds after page save only when the draft is synchronously clean', async () => {
    const pending = deferred()
    const onContinue = vi.fn()
    const save = vi.fn(() => pending.promise)
    renderGuard(save, onContinue)

    await waitFor(() =>
      expect(screen.getByTestId('guard-ready').textContent).toBe('true')
    )
    fireEvent.click(screen.getByRole('button', { name: 'Navigate' }))
    fireEvent.click(screen.getByRole('button', { name: 'Save and continue' }))
    await waitFor(() => expect(save).toHaveBeenCalledTimes(1))
    fireEvent.click(screen.getByRole('button', { name: 'Saved current draft' }))

    await act(async () => pending.resolve())
    expect(onContinue).toHaveBeenCalledTimes(1)
  })
})
